/* tslint:disable:no-output-on-prefix */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import * as _ from 'lodash';
import { Network } from 'vis-network/peer/esm/vis-network';
import { DataSet } from 'vis-data/peer/esm/vis-data';
import {NetworkDiagramConfigurationsService} from './services/network-diagram-configurations/network-diagram-configurations.service';
import {NetworkDiagramStateService} from './services/network-diagram-state/network-diagram-state.service';
import {NetworkDiagramPathDrawingService} from './services/network-diagram-path-drawing/network-diagram-path-drawing.service';
import {NetworkDiagramClusteringService} from './services/network-diagram-clustering/network-diagram-clustering.service';
import {NetworkDiagramSelectionHelperService} from './services/network-diagram-selection-helper/network-diagram-selection-helper.service';

@Component({
  selector: 'network-diagram',
  templateUrl: './network-diagram.component.html',
  styleUrls: ['./network-diagram.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class NetworkDiagramComponent implements OnInit, OnDestroy {

  constructor(private diagramConfigurations: NetworkDiagramConfigurationsService,
              private networkDiagramStateService: NetworkDiagramStateService,
              private networkDiagramPathDrawingService: NetworkDiagramPathDrawingService,
              private networkDiagramClusteringService: NetworkDiagramClusteringService,
              private networkDiagramSelectionHelperService: NetworkDiagramSelectionHelperService) {
  }

  @ViewChild('networkDiagramContainer', {static: true}) networkDiagramContainer: ElementRef;

  @Input() diagramId: string;
  @Input() getClusteredNodePropertiesByNodes;
  @Input() getClusteredEdgePropertiesByEdges;
  @Input() initialDiagramOptions: any = {};

  @Output() onDiagramReady: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDragStart: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDiagramClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() onEdgeDragged: EventEmitter<any> = new EventEmitter<any>();
  @Output() onContextMenu: EventEmitter<any> = new EventEmitter<any>();

  container: any;
  diagram: any;
  advancedConfigurations: any;
  data: any = {
    nodesArray: false,
    edgesArray: false,
    nodes: null,
    edges: null,
    currentPathNodes: [],
    currentPathEdges: [],
    clusters: []
  };

  ngOnInit(): void {
    this.data.nodes = new DataSet([]);
    this.data.edges = new DataSet([]);

    this.constructDiagram();
    this.onDiagramReady.emit({
      diagramApi: {
        getData: this.getData,
        setData: this.setData,
        unselectAll: this.unselectAll,
        updateData: this.updateData,
        updateClusters: this.updateClusters,
        selectNodes: this.selectNodes,
        selectEdges: this.selectEdges,
        select: this.select,
        getConnectionSelection: this.getConnectionSelection,
        getShapeSelection: this.getShapeSelection,
        getSelection: this.getSelection,
        isSingleSelection: this.isSingleSelection,
        getElementsLocation: this.getElementsLocation,
        doShapesDragged: this.doShapesDragged,
        doesClustersStateChanged: this.doesClustersStateChanged,
        addEdgeMode: this.addEdgeMode,
        removeNodes: this.removeNodes,
        removeEdges: this.removeEdges,
        domToCanvasTranslate: this.domToCanvasTranslate,
        clearPath: this.clearPath,
        drawPath: this.drawPath,
        fit: this.fit,
        findNode: this.findNode,
        createCluster: this.createCluster,
        clusterByIds: this.clusterByIds,
        addNodesToCluster: this.addNodesToCluster,
        openCluster: this.openCluster,
        areClustersChanged: this.areClustersChanged,
        updateMapConfigurations: this.updateMapConfigurations,
        updateCluster: this.updateCluster,
        zoomIn: this.zoomIn,
        zoomOut: this.zoomOut
      }
    });
  }

  scopeDestroyForBroadcasts = () => {};
  ngOnDestroy(): void {
    this.scopeDestroyForBroadcasts();
  }

  constructDiagram(): void {
    this.container = this.networkDiagramContainer.nativeElement;
    const defaultDiagramOptions = this.diagramConfigurations.generateNormalSizeConf(this.edgeDragged);
    const initialDiagramVisJsOptions = this.initialDiagramOptions.visjs ? this.initialDiagramOptions.visjs : {};
    const initialDiagramAdvancedOptions = this.initialDiagramOptions.advancedConfigurations ?
      this.initialDiagramOptions.advancedConfigurations : {};
    const diagramOptions = _.merge({}, defaultDiagramOptions, initialDiagramVisJsOptions);
    this.advancedConfigurations = initialDiagramAdvancedOptions;
    this.diagram = new Network(this.container, {nodes: [], edges: []}, diagramOptions);

    this.diagram.on('select', this.selectionChanged);
    this.diagram.on('dragStart', this.onDragStartInner);
    this.diagram.on('click', this.onClick);
    this.diagram.on('dragEnd', this.dragEndCallback);
    this.diagram.on('zoom', this.zoomChanged);
    // this.sharedEventService.on(this, 'EdgeDraggedEvent', this.edgeDragged);
    this.diagram.on('oncontext', this.onContext);
    // this.sharedEventService.on(this, 'disableEditModeInDiagram', this.disableEditMode);
  }

  disableEditMode = () => {
    this.diagram.disableEditMode();
  }

  onDragStartInner = (event) => {
    this.onDragStart.emit({event});
  }

  onClick = (event) => {
    if (event.nodes.length === 0 && event.edges.length === 0) {
      this.diagram.disableEditMode();
      this.onDiagramClick.emit({event});
    }
  }

  unselectAll = () => {
    this.diagram.unselectAll();
    this.selectionChanged(); // needed because no event fired
  }

  selectNodes = (nodesIds) => {
    this.diagram.selectNodes(nodesIds, false);
    this.selectionChanged();
  }

  selectEdges = (edgesIds) => {
    this.diagram.selectEdges(edgesIds);
    this.selectionChanged();
  }

  select = (items) => {
    this.diagram.setSelection(items);
  }

  removeNodes = (ids) => {
    this.data.nodes.remove(ids);
  }

  removeEdges = (ids) => {
    this.data.edges.remove(ids);
  }

  getData = () => {
    return this.data;
  }

  addEdgeMode = () => {
    this.diagram.addEdgeMode();
  }

  edgeDragged = (edgeData) => {
    const draggedEdgeObject = {
      id: edgeData.id,
      from: this.data.nodes.get(edgeData.from),
      to: this.data.nodes.get(edgeData.to)
    };

    this.onEdgeDragged.emit({draggedEdgeObject});
  }

  doShapesDragged = () => {
    return !_.isEqual(this.networkDiagramStateService.getLocationsState(this.diagramId), {});
  }

  doesClustersStateChanged = () => {
    return this.networkDiagramStateService.getClustersState(this.diagramId).changed;
  }

  dragEndCallback = (event) => {
    if (event.nodes.length === 0 && event.edges.length === 0) {
      this.saveViewPosition();
    } else if (event.nodes.length !== 0) {
      this.saveElementsPosition();
    }
  }

  zoomChanged = () => {
    this.saveScale();
  }

  saveScale(): void {
    const scale = this.diagram.getScale();
    this.networkDiagramStateService.setDiagramZoom(this.diagramId, scale);
  }

  saveViewPosition(): void {
    this.networkDiagramStateService.setViewPosition(this.diagramId, this.diagram.getViewPosition());
  }

  saveElementsPosition(): void {
    this.saveNodesLocations();
    this.saveClustersLocations();
  }

  saveClustersLocations(): void {
    const clusterIds = this.data.clusters.map((cluster) => cluster.id);
    const clustersLocations = this.diagram.getPositions(clusterIds);
    this.data.clusters.forEach((cluster) => {
      cluster.x = clustersLocations[cluster.id].x;
      cluster.y = clustersLocations[cluster.id].y;
    });
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
  }

  saveNodesLocations(): void {
    const nodeIds = this.data.nodes.getIds();
    const elementsLocations = this.diagram.getPositions(nodeIds);
    this.networkDiagramStateService.setLocationsState(this.diagramId, elementsLocations);
  }

  getElementsLocation = () => {
    return this.diagram.getPositions(this.data.nodes.getIds());
  }

  selectionChanged = (selection = null) => {
    this.onSelect.emit({
      nodes: this.getShapeSelection(),
      edges: this.getConnectionSelection(),
      ctrlKey: selection && selection.event && selection.event.srcEvent && selection.event.srcEvent.ctrlKey
        ? selection.event.srcEvent.ctrlKey : false
    });
  }

  updateData = (nodes, edges) => {
    this.data.nodesArray = nodes;
    this.data.edgesArray = edges;

    this.restoreLocations();
    this.updateDiagramData();
    this.selectionChanged();
  }

  setData = (nodes, edges, clusters, override) => {
    this.data.nodesArray = nodes;
    this.data.edgesArray = edges;

    if (override) {
      this.saveClusters(clusters);
      this.clearCache();
    } else {
      this.restoreLocations();
    }

    this.initializeDiagramWithData();
  }

  saveClusters(clusters): void {
    this.data.clusters = clusters;
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(clusters), changed: false});
  }

  updateDiagramData(): void {
    this.removeNonExistingElements(this.data.nodesArray, this.data.nodes);
    this.removeNonExistingElements(this.data.edgesArray, this.data.edges);

    const onlyChangedOrNewNodes = this.data.nodesArray.filter((nodeFromServer) => {
      const nodeFromDataSet = this.data.nodes.get(nodeFromServer.id);

      return nodeFromDataSet === null || !_.isEqual(nodeFromDataSet, nodeFromServer);
    });

    const onlyChangedOrNewEdges = this.data.edgesArray.filter((edgesFromServer) => {
      const edgeFromDataSet = this.data.edges.get(edgesFromServer.id);

      return edgeFromDataSet === null || !_.isEqual(edgeFromDataSet, edgesFromServer);
    });

    if (onlyChangedOrNewNodes.length > 0) {
      this.data.nodes.update(onlyChangedOrNewNodes);
    }
    if (onlyChangedOrNewEdges.length > 0) {
      this.data.edges.update(onlyChangedOrNewEdges);
    }

    this.removeNotExistingClusters();

    if (onlyChangedOrNewEdges.length > 0 || onlyChangedOrNewNodes.length > 0) {
      this.updateClustersProperties();
    }
    this.restorePath();
  }

  updateClustersProperties(): void {
    this.networkDiagramClusteringService.updateClustersProperties(this.diagram,
      this.data,
      this.getClusteredNodePropertiesByNodes,
      this.getClusteredEdgePropertiesByEdges);
  }

  updateClusterName(id, name, label): void {
    this.networkDiagramClusteringService.updateClusterProperties(this.diagram, this.data, id, name, label);
  }

  initializeDiagramWithData(): void {
    this.data.edges = new DataSet(this.data.edgesArray);
    this.data.nodes = new DataSet(this.data.nodesArray);

    this.diagram.setData({nodes: this.data.nodes, edges: this.data.edges});

    this.restoreClusters();
    this.restorePath();
    this.adjustMapPosition();
  }

  restorePath(): void {
    if (!!this.data.lastTestRouteState) {
      this.drawPathSimple(this.data.lastTestRouteState);
    }
  }

  restoreClusters(): void {
    this.data.clusters = this.networkDiagramStateService.getClustersState(this.diagramId).clusters;
    this.networkDiagramClusteringService.restoreClusters(this.diagram,
      this.data,
      this.getClusteredNodePropertiesByNodes,
      this.getClusteredEdgePropertiesByEdges);
    this.removeNotExistingClusters();
  }

  adjustMapPosition(): void {
    const scale = this.networkDiagramStateService.getDiagramZoom(this.diagramId) || 0.5;
    const viewPosition = this.networkDiagramStateService.getViewPosition(this.diagramId);

    if (viewPosition) {
      this.diagram.moveTo({position: viewPosition, scale});
    } else {
      this.fit();
    }
  }

  removeNotExistingClusters(): any {
    const allVisibleClusters = this.diagram.clustering.body.nodeIndices.filter((nodeIndex) => {
      return this.diagram.isCluster(nodeIndex);
    });

    let clustersChanged = false;

    this.data.clusters = this.data.clusters.filter((cluster) => {
      if (allVisibleClusters.indexOf(cluster.id) > -1) {
        return true;
      } else {
        clustersChanged = true;
        return false;
      }
    });

    if (clustersChanged) {
      this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
    }
  }

  restoreLocations(): void {
    const savedLocations: any = this.networkDiagramStateService.getLocationsState(this.diagramId);

    this.data.nodesArray.forEach((node) => {
      node.x = savedLocations[node.id] ? savedLocations[node.id].x : node.x;
      node.y = savedLocations[node.id] ? savedLocations[node.id].y : node.y;
    });
  }

  clearCache(): void {
    this.networkDiagramStateService.setLocationsState(this.diagramId, {});
  }

  getShapeSelection = () => {
    return this.networkDiagramSelectionHelperService.getShapeSelection(this.diagram, this.data);
  }

  getConnectionSelection = () => {
    return this.networkDiagramSelectionHelperService.getConnectionSelection(this.diagram, this.data);
  }

  getSelection = () => {
    return this.getShapeSelection().concat(this.getConnectionSelection());
  }

  isSingleSelection = () => {
    const shapeSelection = this.getShapeSelection();
    const connectionsSelection = this.getConnectionSelection();
    return shapeSelection.length + connectionsSelection.length === 1;
  }

  domToCanvasTranslate = (x, y) => {
    const canvasCoordinates = this.diagram.DOMtoCanvas({x, y});
    return {x: canvasCoordinates.x, y: canvasCoordinates.y};
  }

  onContext = (params) => {
    let element;
    const nodeAtClickPoint = this.diagram.getNodeAt(params.pointer.DOM);
    const edgeAtClickPoint = this.diagram.getEdgeAt(params.pointer.DOM);

    if (nodeAtClickPoint || edgeAtClickPoint) {
      if (nodeAtClickPoint) {  // node context menu
        if (this.diagram.isCluster(nodeAtClickPoint)) {
          element = this.data.clusters.find((cluster) => {
            return cluster.id === nodeAtClickPoint;
          });
          element.shapeType = 'cluster';
        } else {
          element = this.data.nodes.get(nodeAtClickPoint);
        }
      } else if (edgeAtClickPoint && !nodeAtClickPoint) {  // edge context menu
        if (edgeAtClickPoint.indexOf('clusterEdge') > -1) {
          params.event.preventDefault();
          params.event.stopPropagation();
        } else {
          element = this.data.edges.get(edgeAtClickPoint);
        }
      }
      this.onContextMenu.emit({contextMenuObject: {element}});
    } else {
      params.event.preventDefault();
      params.event.stopPropagation();
    }
  }

  clearPath = () => {
    this.networkDiagramPathDrawingService.clearPath(this.diagram, this.data, this.getClusteredEdgePropertiesByEdges);
  }

  drawPath = (paths) => {
    if (paths.length === 0) {
      this.clearPath();
      return;
    }

    if (this.advancedConfigurations.drawAnimated) {
      this.drawPathAnimated(paths);
    } else {
      this.drawPathSimple(paths);
    }
  }

  drawPathSimple(paths): void {
    this.networkDiagramPathDrawingService.drawPath(this.diagram, this.data, paths, this.getClusteredEdgePropertiesByEdges);
  }

  drawPathAnimated(paths): void {
    this.networkDiagramPathDrawingService.drawPathAnimated(this.diagram, this.data, paths, this.getClusteredEdgePropertiesByEdges);
  }

  fit = () => {
    this.diagram.fit();
    this.saveScale();
    this.saveViewPosition();
  }

  createCluster = (nodeId, typeToConnect, clusterName) => {
    this.networkDiagramClusteringService.createCluster(this.diagram,
      this.data,
      nodeId,
      typeToConnect,
      this.getClusteredNodePropertiesByNodes,
      this.getClusteredEdgePropertiesByEdges,
      clusterName);
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
    this.restorePath();
  }

  addNodesToCluster = (clusterId, nodeIds) => {
    this.networkDiagramClusteringService.addNodesToCluster(this.diagram,
      this.data,
      clusterId,
      nodeIds,
      this.getClusteredNodePropertiesByNodes,
      this.getClusteredEdgePropertiesByEdges);
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
    this.restorePath();
  }

  updateCluster = (id, name, label) => {
    this.updateClusterName(id, name, label);
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
  }

  zoomIn = () => {
    this.diagram.moveTo({
      scale: Math.min(this.diagram.getScale() + 0.2, 10)
    });
  }

  zoomOut = () => {
    this.diagram.moveTo({
      scale: Math.max(this.diagram.getScale() - 0.2, 0.1)
    });
  }

  updateClusters = (clusters) => {
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(clusters), changed: true});
  }

  clusterByIds = (ids, name) => {
    this.networkDiagramClusteringService.clusterByIds(this.diagram,
      this.data,
      ids,
      this.getClusteredNodePropertiesByNodes,
      this.getClusteredEdgePropertiesByEdges,
      name);
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
    this.restorePath();
    this.diagram.unselectAll();
    this.onSelect.emit({nodes: [], edges: []});
  }

  openCluster = (clusterId) => {
    this.networkDiagramClusteringService.openCluster(this.diagram, this.data, clusterId);
    this.networkDiagramStateService.setClustersState(this.diagramId, {clusters: _.cloneDeep(this.data.clusters), changed: true});
  }

  areClustersChanged = () => {
    return this.networkDiagramStateService.getClustersState(this.diagramId).changed;
  }

  removeNonExistingElements(mostRecentElements, dataSet): void {
    const elementsToRemove = [];
    const elementsIdsObject = {};
    const elementsIds = mostRecentElements.map((node) => node.id);
    const oldElementsIds = dataSet.getIds();

    elementsIds.forEach((nodeId) => {
      elementsIdsObject[nodeId] = true;
    });

    oldElementsIds.forEach((oldNodeId) => {
      if (!elementsIdsObject[oldNodeId]) {
        elementsToRemove.push(oldNodeId);
      }
    });

    dataSet.remove(elementsToRemove);
  }

  findNode = (nodeId) => {
    return this.diagram.clustering.findNode(nodeId);
  }

  updateMapConfigurations = (mapConfigurations) => {
    this.advancedConfigurations = mapConfigurations.advancedConfigurations;
    const initialDiagramVisJsOptions = this.initialDiagramOptions.visjs ? this.initialDiagramOptions.visjs : {};
    const configurations = _.merge({}, this.diagramConfigurations.generateNormalSizeConf(this.edgeDragged), initialDiagramVisJsOptions);
    this.diagram.setOptions(configurations);
  }

}
